'use client';

import {
    Heading,
    HorizontalOverflowCarousel,
    HorizontalOverflowCarouselItem,
    LayoutContainer,
    TextLink,
    TextPassage,
} from '@petsmart-ui/sparky';
import cn from 'classnames';
import { usePathname } from 'next/navigation';

import { trackContentfulClick } from '@libs/analytics';

import { NavCard } from './NavCard';
import { PromoCard } from './PromoCard';
import type { PromoCarouselProps } from './types';
import './PromoCarousel.scss';

export function PromoCarousel({
    header,
    link,
    description,
    leftBlock,
    rightBlock,
    leftBlockTextColor,
}: PromoCarouselProps) {
    const pathname = usePathname();

    return (
        <LayoutContainer className="c-promo-carousel">
            <Heading tagName="h2">{header}</Heading>
            <TextPassage align="center">{description}</TextPassage>
            {link?.link && <TextLink href={link?.link?.webUrl}>{link?.actionText}</TextLink>}

            <div className="c-promo-carousel__content">
                <div className="c-promo-carousel__navs">
                    {leftBlock.map((nav, index) => (
                        <NavCard
                            key={nav.sysId}
                            {...nav}
                            textColor={leftBlockTextColor}
                            handleAnalytics={() =>
                                trackContentfulClick({
                                    position: index + 1,
                                    linkText: nav.header,
                                    linkUrl: nav.link?.webUrl ?? '',
                                    promoTitle: header,
                                    promoVariant: 'storytelling card up - promo carousel - Nav Card',
                                    promoOrigin: pathname,
                                    promoDesc: nav.internalTitle,
                                    promoType: 'nav-card',
                                })
                            }
                        />
                    ))}
                </div>

                <div className="c-promo-carousel__carousel">
                    <HorizontalOverflowCarousel
                        perViewDefault={2}
                        breakpoints={{
                            '560': { gap: 8, perView: 1.3 },
                            '768': { gap: 24, perView: 2.2 },
                            '960': { gap: 24, perView: 2.5 },
                        }}
                    >
                        {rightBlock.map((promo, index) => (
                            <HorizontalOverflowCarouselItem key={promo.sysId}>
                                <PromoCard
                                    {...promo}
                                    handleAnalytics={() =>
                                        trackContentfulClick({
                                            position: index + 1,
                                            linkText: cn(promo.header, promo.bodyText, promo.cta?.text),
                                            linkUrl: promo.cta?.link.webUrl ?? '',
                                            promoTitle: header,
                                            promoVariant: 'storytelling card up - promo carousel - Promo Card',
                                            promoOrigin: pathname,
                                            promoDesc: promo.internalTitle,
                                            promoType: 'c-promo-card',
                                        })
                                    }
                                />
                            </HorizontalOverflowCarouselItem>
                        ))}
                    </HorizontalOverflowCarousel>
                </div>
            </div>
        </LayoutContainer>
    );
}
